import type {FC} from 'react';

import styled from 'styled-components';

import type {WithDomStyleProp} from '../../utils/utility-types';
import type {Props, StyledProps} from './types';

import {LoadingSpinner} from '../LoadingSpinner';
import {useController} from './controller';
import {Icon} from './Icon';
import {web} from './styles';
import {ButtonV2} from './v2';

const StyledButton = styled.button<StyledProps>`
  ${web}
`;

/**
 * @deprecated Use ButtonV2 instead.
 */
const ButtonV1: FC<WithDomStyleProp<Props>> = ({
  children,
  disabled,
  icon,
  iconColor = 'icon2',
  isLoaderReplaceContent = false,
  isLoading,
  onClick,
  scheme = 'primary',
  size = 'm',
  testID,
  variant = 'contained',
  ...rest
}) => {
  const {clickHandler, loaderSize, loaderStroke} = useController({
    onClick,
    scheme,
    size,
  } as Required<Props>);

  const renderSpinner = () =>
    isLoading ? (
      <LoadingSpinner
        size={loaderSize}
        strokeInnerColor={loaderStroke}
        strokeOuterColor={loaderStroke}
      />
    ) : null;

  const renderContent = () => {
    if (isLoaderReplaceContent) {
      return isLoading ? null : children;
    }

    return (
      <>
        {icon ? (
          <Icon
            color={iconColor}
            component={icon}
            size={size}
            withMargin={Boolean(children)}
          />
        ) : null}
        {children}
      </>
    );
  };

  return (
    <StyledButton
      {...rest}
      data-test-id={testID}
      disabled={isLoading || disabled}
      onClick={clickHandler}
      scheme={scheme}
      size={size}
      type="button"
      variant={variant}
    >
      {renderSpinner()}
      {renderContent()}
    </StyledButton>
  );
};

const Button: typeof ButtonV1 & {
  V2: typeof ButtonV2;
} = Object.assign(ButtonV1, {V2: ButtonV2});

export {Button};
